/**
 * Типограф поверх всего отрисованного.
 *
 * Правила из `.claude/rules/typography.md` обязаны работать на ЛЮБОМ
 * содержимом страницы, а его на ней три сорта, и раньше каждый обслуживался
 * по-своему:
 *
 *   - наши подписи — расставлялись руками при написании и регулярно
 *     забывались;
 *   - текст генератора — прогонялся по снимку `ReportData`;
 *   - строки, которые страница собирает сама («Из 128 дней периода команда
 *     касалась кабинета 35 дней»), — не обслуживались вовсе: они рождаются
 *     уже после того, как проход по данным закончился.
 *
 * Проход по отрисованному тексту закрывает все три разом и снимает главную
 * опасность прохода по данным: там приходилось угадывать по имени ключа, что
 * является текстом, а что идентификатором, и первое же новое поле от
 * генератора рисковало получить неразрывный пробел внутрь значения. В DOM
 * попадает только то, что человек читает, — испортить ссылку или ISO-дату
 * физически нечем.
 *
 * ЕДИНИЦА ОБРАБОТКИ — АБЗАЦ, А НЕ ТЕКСТОВЫЙ УЗЕЛ. React режет предложение на
 * узлы всюду, где в него подставлено значение: «Из » + «128 дней» + «
 * периода команда касалась». Поузловой проход на таком тексте ошибается
 * дважды: не склеивает «Из» со следующим словом, потому что слова в его узле
 * нет, и считает концом абзаца конец узла, отчего правило «последние два
 * слова» срабатывает посреди предложения. Поэтому узлы сначала собираются в
 * абзац по общему блочному предку, правила применяются к нему целиком, а
 * результат раскладывается обратно.
 *
 * @format
 */

import {useEffect, type RefObject} from 'react';

import {typograph} from './typograph';

/**
 * Куда типограф не заходит.
 *
 * Поля ввода и код — это не проза: неразрывный пробел в значении инпута
 * уедет на сервер, а в коде он меняет смысл. Атрибут `data-no-typograph`
 * оставлен как ручной выключатель для случаев, которых мы ещё не знаем.
 */
const SKIP = 'input, textarea, select, option, code, pre, script, style, [data-no-typograph]';

/** Word joiner — единственный символ, который типограф ВСТАВЛЯЕТ, а не заменяет. */
const WJ = '\u2060';

/**
 * Теги, которые не разрывают строку.
 *
 * По тегу, а не по вычисленному `display`: `getComputedStyle` на каждый
 * элемент документа стоит дорого — на отчёте это сотни вызовов при каждой
 * перерисовке, и в тестовой среде проход не укладывался в секунды.
 *
 * Цена решения: `<div>`, которому стилями задан `display: inline`, будет
 * посчитан границей абзаца. В отчёте таких нет — строчный текст набирается
 * `span`-ами, — а появится, и правило переноса просто не свяжет пару слов
 * через эту границу.
 */
const INLINE_TAGS = new Set([
  'SPAN',
  'A',
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'SMALL',
  'SUB',
  'SUP',
  'MARK',
  'ABBR',
  'TIME',
  'BDI',
  'BDO',
  'WBR',
]);

/**
 * Заголовки: им правило последних двух слов не достаётся вовсе.
 *
 * Правило написано для абзаца: там строка длинная, и одинокое слово на
 * последней строке читается как обрыв. На кегле заголовка неразрывная пара —
 * готовое переполнение: «рекламного\u00A0кабинета» при 44px занимает 448px и
 * в колонку телефона (279px) не помещается никак. Перенести нельзя, и вбок
 * уезжает документ.
 *
 * По тегу, а не по вычисленному кеглю: `getComputedStyle` на каждый абзац —
 * это сотни обращений к раскладке при каждой перерисовке, и в тестовой среде
 * проход переставал укладываться в секунды. Крупный текст вне заголовочного
 * тега (вердикт оценки) прикрыт вторым предохранителем — длиной пары, см.
 * `typograph`.
 */
const HEADINGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

/** Ближайший предок, внутри которого течёт одна строка. */
function blockOf(node: Text, root: HTMLElement): Element {
  let element = node.parentElement;
  while (element && element !== root && INLINE_TAGS.has(element.tagName)) {
    element = element.parentElement;
  }
  return element ?? root;
}

/**
 * Возвращает типографированный текст обратно по узлам.
 *
 * Сопоставление посимвольное, а не по смещениям: типограф вставляет word
 * joiner вокруг дефиса, и после первого же составного слова смещения
 * разъезжаются. Зато вставка — единственное, что меняет длину: остальные
 * правила подменяют символ на месте (пробел на неразрывный). Поэтому идём
 * двумя указателями и относим вставленные символы к тому узлу, в котором
 * стоял их дефис.
 *
 * `null` — если сопоставление не сошлось. Такого быть не должно, но лучше
 * оставить абзац нетронутым, чем разложить его как попало.
 */
function redistribute(parts: string[], source: string, result: string): string[] | null {
  const out: string[] = [];
  let si = 0;
  let ri = 0;

  for (const part of parts) {
    const start = ri;

    for (let k = 0; k < part.length; k += 1) {
      // Вставленные word joiner'ы к исходному тексту не относятся —
      // проматываем их, но только если в исходнике на этом месте не свой.
      while (ri < result.length && result[ri] === WJ && source[si] !== WJ) ri += 1;
      if (ri >= result.length) return null;
      ri += 1;
      si += 1;
    }

    // Хвостовой клей достаётся узлу, которым кончается дефис.
    while (ri < result.length && result[ri] === WJ && source[si] !== WJ) ri += 1;

    out.push(result.slice(start, ri));
  }

  return ri === result.length ? out : null;
}

/** Один проход: собрать абзацы, типографировать, разложить обратно. */
function typographTree(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const paragraphs = new Map<Element, Text[]>();

  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const text = node as Text;
    // Пустые узлы пропускаем: их в дереве большинство (переносы строк между
    // элементами), а работы по ним нет. В абзац они тоже не идут — иначе
    // сдвинут сопоставление ничем.
    if (!text.nodeValue.trim()) continue;
    if (text.parentElement?.closest(SKIP) != null) continue;

    const block = blockOf(text, root);
    const group = paragraphs.get(block);
    if (group) group.push(text);
    else paragraphs.set(block, [text]);
  }

  paragraphs.forEach((nodes, block) => {
    const parts = nodes.map(node => node.nodeValue);
    const source = parts.join('');
    const result = typograph(source, {tieLastWords: !HEADINGS.has(block.tagName)});
    if (result === source) return;

    const next = redistribute(parts, source, result);
    if (!next) return;

    // Пишем только при изменении. Это и защита от лишней работы, и условие
    // сходимости: типограф идемпотентен, поэтому второй проход по своему же
    // результату ничего не меняет и наблюдатель за мутациями замолкает.
    nodes.forEach((node, index) => {
      if (next[index] !== node.nodeValue) node.nodeValue = next[index];
    });
  });
}

/**
 * Держит типографику на всём поддереве, пока компонент жив.
 *
 * `useEffect`, а не `useLayoutEffect`: правка текстовых узлов не влияет на
 * геометрию — служебные символы невидимы, — зато поздний проход не
 * задерживает первую отрисовку большого документа.
 *
 * React перезаписывает текстовый узел при каждом обновлении, поэтому одного
 * прохода мало: наблюдатель ловит новую отрисовку и типографирует её. На
 * время собственной правки он отключается — иначе она вернулась бы к нему
 * же входящим событием.
 */
export function useTypographDom(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const observer = new MutationObserver(() => {
      observer.disconnect();
      typographTree(root);
      observer.observe(root, {childList: true, characterData: true, subtree: true});
    });

    typographTree(root);
    observer.observe(root, {childList: true, characterData: true, subtree: true});

    return () => observer.disconnect();
  }, [ref]);
}
